import React, { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';
import VisitOMSLogo from './VisitOMS_logo.png';

const MAGIC_LINK_URL = 'https://ops.visitoms.com/session-expired';

const API_BASE = '/api';

const getStoredTrustedDeviceToken = () => {
  const key = 'trusted_device_token';
  let token = localStorage.getItem(key);
  if (!token) {
    token = `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, token);
  }
  return token;
};

const getDeviceLabel = () => {
  const platform = navigator.userAgentData?.platform || navigator.platform || 'Web';
  return `Web Browser (${platform})`;
};

const getPlatform = () => 'web';

const postJson = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    const err = new Error(data?.message || 'Request failed');
    err.response = { data, status: response.status };
    throw err;
  }

  return { data, status: response.status };
};

const navigateByRole = (navigate, user) => {
  if (!user) return;
  if (user.role === 'super_admin') navigate('/super');
  else if (user.role === 'admin') navigate('/admin');
  else if (user.role === 'teacher') navigate('/teacher');
  else if (user.role === 'parent') navigate('/parent');
};

const Login = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [location, setLocation] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [geoPermission, setGeoPermission] = useState('prompt');
  const [locationChecking, setLocationChecking] = useState(true);

  const [needsOrgKey, setNeedsOrgKey] = useState(false);
  const [organizationKey, setOrganizationKey] = useState('');
  const orgKeyRef = useRef(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA / pre-auth flow
  const [authStep, setAuthStep] = useState('login'); // login | setup | verify | backupCodes
  const [setupToken, setSetupToken] = useState('');
  const [pending2faToken, setPending2faToken] = useState('');
  const [mfaUser, setMfaUser] = useState(null);
  const [otpAuthUrl, setOtpAuthUrl] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [newBackupCodes, setNewBackupCodes] = useState([]);
  const [pendingFinalSession, setPendingFinalSession] = useState(null);

  const trustedDeviceToken = useMemo(() => getStoredTrustedDeviceToken(), []);
  const deviceLabel = useMemo(() => getDeviceLabel(), []);
  const devicePlatform = useMemo(() => getPlatform(), []);

  useEffect(() => {
    checkGeolocationPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkGeolocationPermission = async () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser.');
      setLocationChecking(false);
      setGeoPermission('denied');
      return;
    }

    if (navigator.permissions && navigator.permissions.query) {
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        setGeoPermission(status.state);

        if (status.state === 'granted') {
          getGeolocation();
        } else {
          setLocationChecking(false);
        }

        status.onchange = () => {
          setGeoPermission(status.state);
          if (status.state === 'granted') {
            getGeolocation();
          } else {
            setLocation(null);
          }
        };
      } catch (err) {
        console.warn('Permissions API error, falling back:', err);
        setLocationChecking(false);
      }
    } else {
      setLocationChecking(false);
    }
  };

  const getGeolocation = () => {
    if (!navigator.geolocation) return;
    setLocationChecking(true);
    setGeoError('');

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
        setGeoPermission('granted');
        setLocationChecking(false);
      },
      (err) => {
        console.error('Geolocation error', err);
        setGeoError('Unable to get your location. Please allow location access in your browser settings.');
        setGeoPermission('denied');
        setLocation(null);
        setLocationChecking(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  const persistSessionAndNavigate = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    if (location) {
      localStorage.setItem('location', JSON.stringify(location));
    }
    navigateByRole(navigate, user);
  };

  const resetMfaFlow = () => {
    setAuthStep('login');
    setSetupToken('');
    setPending2faToken('');
    setMfaUser(null);
    setOtpAuthUrl('');
    setManualEntryKey('');
    setTotpCode('');
    setBackupCode('');
    setUseBackupCode(false);
    setNewBackupCodes([]);
    setPendingFinalSession(null);
  };

  const startPreauthSetup = async (incomingSetupToken, incomingUser) => {
    setLoading(true);
    setError('');

    try {
      const response = await postJson(`${API_BASE}/auth/2fa/setup/start-preauth`, {
        setup_token: incomingSetupToken,
      });

      setSetupToken(incomingSetupToken);
      setMfaUser(incomingUser || null);
      setOtpAuthUrl(response.data?.otp_auth_url || '');
      setManualEntryKey(response.data?.manual_entry_key || '');
      setTotpCode('');
      setAuthStep('setup');
    } catch (err) {
      console.error('Pre-auth setup start error:', err?.response?.data || err);
      setError(err?.response?.data?.message || 'Unable to start 2FA setup.');
      setAuthStep('login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setError('');

    if (!location) {
      setError('Geolocation is not enabled. Please enable it to proceed.');
      return;
    }

    if (needsOrgKey && !organizationKey.trim()) {
      setError('Organization key is required for this email.');
      orgKeyRef.current?.focus?.();
      return;
    }

    setLoading(true);

    try {
      const response = await postJson(`${API_BASE}/login`, {
        email,
        password,
        latitude: location.lat,
        longitude: location.lon,
        organization_key: needsOrgKey ? organizationKey.trim() : undefined,
        trusted_device_token: trustedDeviceToken,
        device_label: deviceLabel,
        platform: devicePlatform,
      });

      const data = response.data || {};

      if (data?.token && data?.user) {
        persistSessionAndNavigate(data.token, data.user);
        return;
      }

      if (data?.requires_2fa && data?.pending_2fa_token) {
        setPending2faToken(data.pending_2fa_token);
        setMfaUser(data.user || null);
        setTotpCode('');
        setBackupCode('');
        setUseBackupCode(false);
        setAuthStep('verify');
        return;
      }

      setError('Invalid response from server.');
    } catch (err) {
      const data = err?.response?.data || {};
      console.error('Login error:', data || err.message || err);

      if (data.code === 'ORG_KEY_REQUIRED' || data.code === 'ORG_KEY_INVALID') {
        setNeedsOrgKey(true);
        setError(data.message || 'Organization key is required.');
        setTimeout(() => orgKeyRef.current?.focus?.(), 50);
        setLoading(false);
        return;
      }

      if (data.code === 'MFA_ENROLLMENT_REQUIRED' && data.setup_token) {
        await startPreauthSetup(data.setup_token, data.user || null);
        return;
      }

      setError(data.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPreauthSetup = async () => {
    setError('');

    if (!totpCode.trim()) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setLoading(true);

    try {
      const response = await postJson(`${API_BASE}/auth/2fa/setup/confirm-preauth`, {
        setup_token: setupToken,
        code: totpCode.trim(),
        remember_device: rememberDevice,
        trusted_device_token: trustedDeviceToken,
        device_label: deviceLabel,
        platform: devicePlatform,
      });

      const data = response.data || {};

      if (!data?.token || !data?.user) {
        setError('Invalid response from server.');
        setLoading(false);
        return;
      }

      setNewBackupCodes(Array.isArray(data.backup_codes) ? data.backup_codes : []);
      setPendingFinalSession({
        token: data.token,
        user: data.user,
      });
      setAuthStep('backupCodes');
    } catch (err) {
      console.error('Pre-auth setup confirm error:', err?.response?.data || err);
      setError(err?.response?.data?.message || 'Unable to confirm 2FA setup.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async () => {
    setError('');

    if (useBackupCode) {
      if (!backupCode.trim()) {
        setError('Enter a backup code.');
        return;
      }
    } else if (!totpCode.trim()) {
      setError('Enter your 6-digit verification code.');
      return;
    }

    setLoading(true);

    try {
      const endpoint = useBackupCode
        ? `${API_BASE}/auth/2fa/backup-code/verify`
        : `${API_BASE}/auth/2fa/verify`;

      const body = useBackupCode
        ? {
            pending_2fa_token: pending2faToken,
            backup_code: backupCode.trim(),
            remember_device: rememberDevice,
            trusted_device_token: trustedDeviceToken,
            device_label: deviceLabel,
            platform: devicePlatform,
          }
        : {
            pending_2fa_token: pending2faToken,
            code: totpCode.trim(),
            remember_device: rememberDevice,
            trusted_device_token: trustedDeviceToken,
            device_label: deviceLabel,
            platform: devicePlatform,
          };

      const response = await postJson(endpoint, body);
      const data = response.data || {};

      if (!data?.token || !data?.user) {
        setError('Invalid response from server.');
        setLoading(false);
        return;
      }

      persistSessionAndNavigate(data.token, data.user);
    } catch (err) {
      console.error('2FA verify error:', err?.response?.data || err);
      setError(err?.response?.data?.message || 'Unable to verify the code.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueAfterBackupCodes = () => {
    if (!pendingFinalSession?.token || !pendingFinalSession?.user) {
      setError('Missing final session data.');
      return;
    }
    persistSessionAndNavigate(pendingFinalSession.token, pendingFinalSession.user);
  };

  const canSubmitLogin =
    !loading &&
    email &&
    password &&
    location &&
    geoPermission === 'granted' &&
    (!needsOrgKey || organizationKey.trim());

  const onEnterLogin = (e) => {
    if (e.key === 'Enter' && canSubmitLogin) {
      e.preventDefault();
      handleLogin();
    }
  };

  const openMagicLink = () => {
    window.location.href = MAGIC_LINK_URL;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-slate-50 via-white to-slate-100">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600" />

          <div className="p-6 md:p-7">
            <div className="flex flex-col items-center justify-center mb-6">
              <img
                src={VisitOMSLogo}
                alt="VisitOMS logo"
                className="w-16 h-16 md:w-20 md:h-20 mb-3 object-contain"
              />
              <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 text-center">
                VisitOMS EarlyCare
              </h2>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Version v2.51</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                  Secure Login
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-2 text-center leading-snug">
                Admin &amp; Teachers (EI • PI • ABA • OT • PT • ST)
              </p>
            </div>

            {authStep === 'login' && (
              <>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
                <input
                  type="email"
                  placeholder="name@company.com"
                  className="w-full p-3 mb-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={onEnterLogin}
                  autoComplete="email"
                />

                <label className="block text-xs font-semibold text-slate-600 mb-1">Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  className="w-full p-3 mb-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={onEnterLogin}
                  autoComplete="current-password"
                />

                <div className="mb-4 text-xs text-slate-700 border border-slate-200 rounded-2xl p-3 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Location status</span>
                    {locationChecking ? (
                      <span className="italic text-slate-500">Checking…</span>
                    ) : geoPermission === 'granted' && location ? (
                      <span className="text-emerald-700 font-bold">OK</span>
                    ) : geoPermission === 'denied' ? (
                      <span className="text-rose-700 font-bold">Denied</span>
                    ) : (
                      <span className="text-amber-700 font-bold">Not set</span>
                    )}
                  </div>

                  {location && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      Lat: {location.lat.toFixed(5)}, Lon: {location.lon.toFixed(5)}
                    </div>
                  )}

                  {geoError && <div className="mt-1 text-[11px] text-rose-700">{geoError}</div>}

                  {(!location || geoPermission !== 'granted') && !locationChecking && (
                    <button
                      type="button"
                      onClick={getGeolocation}
                      className="mt-2 inline-flex items-center justify-center rounded-xl border border-sky-500 px-3 py-1.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-50"
                    >
                      Enable location
                    </button>
                  )}

                  {geoPermission === 'denied' && !geoError && (
                    <div className="mt-1 text-[11px] text-amber-700">
                      Location access is denied in your browser settings.
                    </div>
                  )}
                </div>

                {needsOrgKey && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Organization key
                    </label>
                    <input
                      ref={orgKeyRef}
                      type="text"
                      placeholder="e.g. default-client, tipinfant…"
                      className="w-full p-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
                      value={organizationKey}
                      onChange={(e) => setOrganizationKey(e.target.value)}
                      onKeyDown={onEnterLogin}
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Your email is used in multiple organizations. Enter the key given by your admin.
                    </p>
                  </div>
                )}

                {error && (
                  <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2">
                    <p className="text-rose-700 text-sm font-semibold">Login failed</p>
                    <p className="text-rose-700 text-xs mt-0.5">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleLogin}
                  disabled={!canSubmitLogin}
                  className={`w-full py-3 rounded-2xl font-extrabold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    canSubmitLogin
                      ? 'bg-sky-600 hover:bg-sky-700 text-white focus:ring-sky-500'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>

                <div className="mt-5 pt-5 border-t border-slate-200">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-extrabold text-slate-900">Parent or Teacher account?</p>
                        <p className="text-[12px] text-slate-600 mt-1 leading-snug">
                          If you were logged out or don’t have a password, request a secure Magic Link by email.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={openMagicLink}
                      className="mt-3 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-extrabold text-slate-900 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      Get Magic Link (Parents & Teachers)
                    </button>

                    <div className="mt-2 text-[11px] text-slate-500">
                      Link: <span className="font-mono">{MAGIC_LINK_URL}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-center">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open('https://visitoms.com', '_blank');
                    }}
                    className="text-xs font-semibold text-sky-700 hover:text-sky-800 hover:underline"
                  >
                    About VisitOMS EarlyCare
                  </a>
                </div>
              </>
            )}

            {authStep === 'setup' && (
              <>
                <div className="mb-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3">
                  <p className="text-sm font-bold text-slate-900">Set up two-factor authentication</p>
                  <p className="text-xs text-slate-700 mt-1">
                    Your organization requires MFA. Add this account to your authenticator app, then enter the 6-digit code below.
                  </p>
                  {mfaUser?.email && (
                    <p className="text-[11px] text-slate-500 mt-2">
                      Account: <span className="font-semibold">{mfaUser.email}</span>
                    </p>
                  )}
                </div>

                <label className="block text-xs font-semibold text-slate-600 mb-1">Manual setup key</label>
                <textarea
                  readOnly
                  value={manualEntryKey}
                  className="w-full p-3 mb-2 border border-slate-200 rounded-xl bg-slate-50 text-xs text-slate-700 focus:outline-none"
                  rows={3}
                />
                <p className="mb-4 text-[11px] text-slate-500">
                  Open Google Authenticator, Microsoft Authenticator, 1Password, or a similar app and add a new account using this key.
                </p>

				  {otpAuthUrl && (
				    <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
				      <p className="text-[11px] font-semibold text-slate-600 mb-2">Scan QR code</p>

				      <div className="flex justify-center bg-white p-3 rounded-xl border border-slate-200">
				        <QRCode value={otpAuthUrl} size={180} />
				      </div>

				      <p className="text-[11px] text-slate-500 mt-3">
				        If you are on the same phone, use the manual setup key instead of scanning.
				      </p>

				      <div className="mt-3">
				        <p className="text-[11px] font-semibold text-slate-600 mb-1">OTP Auth URI</p>
				        <div className="text-[10px] text-slate-500 break-all">{otpAuthUrl}</div>
				      </div>
				    </div>
				  )}

                <label className="block text-xs font-semibold text-slate-600 mb-1">6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="123456"
                  className="w-full p-3 mb-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                />

                <label className="flex items-center gap-2 mb-4 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                  />
                  Remember this device for 30 days
                </label>

                {error && (
                  <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2">
                    <p className="text-rose-700 text-sm font-semibold">Setup failed</p>
                    <p className="text-rose-700 text-xs mt-0.5">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleConfirmPreauthSetup}
                  disabled={loading || !totpCode.trim()}
                  className={`w-full py-3 rounded-2xl font-extrabold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    !loading && totpCode.trim()
                      ? 'bg-sky-600 hover:bg-sky-700 text-white focus:ring-sky-500'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {loading ? 'Confirming…' : 'Complete 2FA Setup'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetMfaFlow();
                    setError('');
                  }}
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Back to login
                </button>
              </>
            )}

            {authStep === 'verify' && (
              <>
                <div className="mb-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3">
                  <p className="text-sm font-bold text-slate-900">Two-factor verification</p>
                  <p className="text-xs text-slate-700 mt-1">
                    Enter the code from your authenticator app to continue.
                  </p>
                  {mfaUser?.email && (
                    <p className="text-[11px] text-slate-500 mt-2">
                      Account: <span className="font-semibold">{mfaUser.email}</span>
                    </p>
                  )}
                </div>

                <div className="mb-4 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackupCode(false);
                      setError('');
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border ${
                      !useBackupCode
                        ? 'bg-sky-600 text-white border-sky-600'
                        : 'bg-white text-slate-700 border-slate-200'
                    }`}
                  >
                    Authenticator code
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackupCode(true);
                      setError('');
                    }}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border ${
                      useBackupCode
                        ? 'bg-sky-600 text-white border-sky-600'
                        : 'bg-white text-slate-700 border-slate-200'
                    }`}
                  >
                    Backup code
                  </button>
                </div>

                {!useBackupCode ? (
                  <>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">6-digit code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="123456"
                      className="w-full p-3 mb-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                    />
                  </>
                ) : (
                  <>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Backup code</label>
                    <input
                      type="text"
                      placeholder="ABCD-1234"
                      className="w-full p-3 mb-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
                      value={backupCode}
                      onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                    />
                  </>
                )}

                <label className="flex items-center gap-2 mb-4 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                  />
                  Remember this device for 30 days
                </label>

                {error && (
                  <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2">
                    <p className="text-rose-700 text-sm font-semibold">Verification failed</p>
                    <p className="text-rose-700 text-xs mt-0.5">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleVerify2fa}
                  disabled={loading || (!useBackupCode && !totpCode.trim()) || (useBackupCode && !backupCode.trim())}
                  className={`w-full py-3 rounded-2xl font-extrabold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    !loading && ((!useBackupCode && totpCode.trim()) || (useBackupCode && backupCode.trim()))
                      ? 'bg-sky-600 hover:bg-sky-700 text-white focus:ring-sky-500'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {loading ? 'Verifying…' : 'Verify and Sign In'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetMfaFlow();
                    setError('');
                  }}
                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Back to login
                </button>
              </>
            )}

            {authStep === 'backupCodes' && (
              <>
                <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <p className="text-sm font-bold text-slate-900">2FA setup complete</p>
                  <p className="text-xs text-slate-700 mt-1">
                    Save these backup codes now. Each code can be used once if you lose access to your authenticator app.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    {newBackupCodes.map((code) => (
                      <div
                        key={code}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-mono text-slate-800 text-center"
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleContinueAfterBackupCodes}
                  className="w-full py-3 rounded-2xl font-extrabold bg-sky-600 hover:bg-sky-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                >
                  I saved my backup codes
                </button>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-4">
          © {new Date().getFullYear()} VisitOMS • Secure access
        </p>
      </div>
    </div>
  );
};

export default Login;








